/**
 * The "Upgrading" chip in the top bar, shown only while the player has an
 * upgrade running: the count and the soonest time left. It opens a list of
 * every own upgrade in progress (`view/upgradesInProgress.ts`), soonest first:
 * a bar, the time left and the match-clock time it finishes ("paused" while
 * under attack). A row locates its building; Cancel refunds it.
 */
import { useEffect, useRef, useState } from 'react';
import type { Command } from '@/game/sim';
import type { Frame } from '@/game/view/frame';
import { clockLabel } from '@/game/view/matchClock';
import { clockText } from '@/game/view/upgradeProgress';
import { type UpgradeInProgress, upgradesInProgress } from '@/game/view/upgradesInProgress';
import { useSimSelector } from './app/simFrame';

function Row({
  row,
  onLocate,
  onCancel,
}: {
  row: UpgradeInProgress;
  onLocate: () => void;
  onCancel: () => void;
}) {
  const { paused, fraction } = row;
  return (
    <li className="flex items-center gap-1 rounded-lg bg-fill px-1 py-1 hover:bg-fill-hover">
      <button
        type="button"
        onClick={onLocate}
        title="Locate building"
        aria-label={`Locate ${row.title}`}
        className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_4.5rem] items-center gap-x-2 px-1 text-left"
      >
        <span className="truncate">{row.title}</span>
        <span className="text-right">
          <b className={`block font-medium tabular-nums ${paused ? 'text-warn' : 'text-accent'}`}>
            {clockText(row.secondsLeft)}
          </b>
          <small className="block text-[10px] text-fg-subtle tabular-nums">
            {row.finishClock === null
              ? 'paused'
              : row.afterEnd
                ? 'after the match'
                : `at ${clockLabel(row.finishClock)}`}
          </small>
        </span>
        <span
          aria-hidden="true"
          className="col-span-2 mt-1 h-1 overflow-hidden rounded-full bg-fill-hover"
        >
          <span
            className={`block h-full rounded-full ${paused ? 'bg-warn bg-[repeating-linear-gradient(135deg,var(--color-warn)_0_5px,color-mix(in_srgb,var(--color-warn)_45%,transparent)_5px_10px)]' : 'bg-accent'}`}
            style={{ width: `${fraction * 100}%` }}
          />
        </span>
      </button>
      <button
        type="button"
        aria-label={`Cancel ${row.title} and refund`}
        title="Cancel and refund"
        onClick={onCancel}
        className="btn btn-ghost btn-icon h-6 w-6 shrink-0 text-xs"
      >
        ✕
      </button>
    </li>
  );
}

const rowsOf = (f: Frame) => upgradesInProgress(f.view, f.rules, f.playerId, f.cityNumbers);

/** Same rows to the second: a tick that changes nothing shown doesn't re-render. */
function sameRows(a: UpgradeInProgress[], b: UpgradeInProgress[]): boolean {
  return (
    a.length === b.length &&
    a.every((r, i) => {
      const o = b[i];
      return (
        r.buildingId === o.buildingId &&
        r.title === o.title &&
        r.paused === o.paused &&
        Math.ceil(r.secondsLeft) === Math.ceil(o.secondsLeft) &&
        Math.round(r.fraction * 100) === Math.round(o.fraction * 100) &&
        r.finishClock === o.finishClock
      );
    })
  );
}

export function UpgradingPill({
  dispatch,
  onLocate,
}: {
  dispatch: (c: Command) => void;
  /** Select the building and pan to it. */
  onLocate: (buildingId: string) => void;
}) {
  const rows = useSimSelector(rowsOf, sameRows);
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const shown = open && rows.length > 0;
  // The list doesn't wait open for the next upgrade.
  useEffect(() => {
    if (rows.length === 0) setOpen(false);
  }, [rows.length]);
  useEffect(() => {
    if (!shown) return;
    const onDown = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    // Capture phase, and stopped: the hotkeys' window handler must not also
    // clear the selection or cancel placement on the Esc that closes the list.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [shown]);
  if (rows.length === 0) return null;
  // Rows run soonest first, paused last: a paused one leads only when all are.
  const soonest = rows[0];

  return (
    <div ref={root} className="relative flex shrink-0">
      <button
        type="button"
        aria-expanded={shown}
        title="Upgrades in progress"
        onClick={() => setOpen((o) => !o)}
        className={`chip gap-1.5 transition ${shown ? 'chip-selected' : 'hover:bg-fill-hover'}`}
      >
        <span aria-hidden="true">⏳</span>
        <span className="sr-only min-[1100px]:not-sr-only">Upgrading</span>
        <span className="tabular-nums text-accent">{rows.length}</span>
        <span
          className={`hidden tabular-nums min-[700px]:inline ${soonest.paused ? 'text-warn' : 'text-fg-muted'}`}
        >
          {clockText(soonest.secondsLeft)}
        </span>
      </button>
      {shown && (
        <div className="panel pointer-events-auto absolute left-0 top-full mt-2.5 flex max-h-[60vh] w-[min(26rem,calc(100vw-1.5rem))] flex-col gap-1.5 p-3 text-xs">
          <p className="panel-title">
            Upgrading now <span className="tabular-nums text-fg-subtle">{rows.length}</span>
          </p>
          <ul className="flex min-h-0 flex-col gap-1 overflow-y-auto">
            {rows.map((row) => (
              <Row
                key={row.buildingId}
                row={row}
                onLocate={() => {
                  onLocate(row.buildingId);
                  setOpen(false);
                }}
                onCancel={() => dispatch({ type: 'cancelUpgrade', buildingId: row.buildingId })}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
